/**
 * ProjectSelector — compact pill that shows the current project and opens
 * a menu of active projects. Sits in the AppTopBar and in the Founder
 * Console TopStatusBar (same component, same behavior).
 */
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Folder, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useActiveProject } from "@/context/ActiveProjectContext";

export default function ProjectSelector({ compact = false }) {
    const { projects, activeProject, setActiveId, loading } = useActiveProject();
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (btnRef.current?.contains(e.target)) return;
            if (menuRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const active = projects.filter((p) => p.status !== "archived");
    const label = loading
        ? "loading…"
        : activeProject?.name || "No project";

    return (
        <div className="relative shrink-0" data-testid="project-selector">
            <button
                ref={btnRef}
                type="button"
                data-testid="project-selector-btn"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={activeProject?.description || activeProject?.name || "Select active project"}
                className={`qoryn-panel rounded-full flex items-center gap-2 transition-colors qoryn-focus hover:bg-white/[0.04] ${
                    compact ? "px-2.5 py-1" : "px-3 py-1.5"
                }`}
            >
                <Folder className="h-3 w-3 text-qoryn-cyan/70" strokeWidth={1.75} />
                <span className="font-sans text-[12px] text-qoryn-text truncate max-w-[180px]">
                    {label}
                </span>
                <ChevronDown className="h-3 w-3 text-qoryn-textMuted" strokeWidth={1.75} />
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        ref={menuRef}
                        data-testid="project-selector-menu"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.14 }}
                        role="listbox"
                        className="absolute right-0 mt-2 w-72 max-h-[60vh] overflow-y-auto z-50 rounded-xl border border-qoryn-border/70 bg-[#0a0d10]/95 backdrop-blur-xl shadow-2xl shadow-black/60"
                    >
                        <div className="px-3 py-2 border-b border-qoryn-border/50 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                            Active projects
                        </div>
                        <ul className="py-1">
                            {active.length === 0 && (
                                <li className="px-3 py-3 font-sans text-[12px] text-qoryn-textDim">
                                    No projects yet.
                                </li>
                            )}
                            {active.map((p) => {
                                const isActive = p.id === activeProject?.id;
                                return (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            data-testid={`project-selector-option-${p.slug}`}
                                            onClick={() => { setActiveId(p.id); setOpen(false); }}
                                            className={`w-full text-left flex items-center gap-2 px-3 py-2 transition-colors ${
                                                isActive
                                                    ? "bg-qoryn-cyan/10 text-qoryn-text"
                                                    : "text-qoryn-textDim hover:bg-white/[0.04] hover:text-qoryn-text"
                                            }`}
                                        >
                                            <span className="flex-1 min-w-0 truncate font-sans text-[13px]">
                                                {p.name}
                                            </span>
                                            {isActive && <Check className="h-3.5 w-3.5 text-qoryn-cyan shrink-0" />}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="border-t border-qoryn-border/50 py-1">
                            <button
                                type="button"
                                data-testid="project-selector-manage"
                                onClick={() => { setOpen(false); navigate("/projects"); }}
                                className="w-full text-left flex items-center gap-2 px-3 py-2 text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.04] transition-colors qoryn-focus"
                            >
                                <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
                                <span className="font-sans text-[12px]">Manage projects</span>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
